import { SelectColorGroup, SelectColorGroupItem } from '@lastbrand/web-ui/select-color-group'
import { StarRating, type StarRatingValue } from '@lastbrand/web-ui/star-rating'
import { Tag } from '@lastbrand/web-ui/tag'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  RefObject,
  AnchorHTMLAttributes,
  FunctionComponent,
  Fragment,
  type ReactNode,
} from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { ProductCardLink } from '@src/components/product-card/product-card-actions/product-card-link/product-card-link'
import { ProductCardHoverProvider } from '@src/components/product-card/product-card-context/product-card-hover.context'
import {
  getAllVariantIds,
  getImagesForOptions,
  getInitialSelectedVariant,
  getVariantTags,
} from '@src/components/product-card/product-card.helpers'
import { useRefreshedTags } from '@src/components/product-card/product-card.hooks'
import { CardVariant } from '@src/components/product-card/product-card.types'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { AddToCartButton, addToCartButtonHoverRevealParentCss } from '@src/ds/blocks/add-to-cart-button'
import { AddToWishlistButton } from '@src/ds/blocks/add-to-wishlist-button'
import { ImageTile, productTileImageHoverSwapCss } from '@src/ds/blocks/image-tile'
import { ProductTilePrice } from '@src/ds/blocks/price'
import { ProductTileTitle } from '@src/ds/blocks/title'
import type { ProductTileVariant } from '@src/ds/types/product-tile'
import { useStatsigDynamicHolidayConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-holiday-config.hook'
import { useStatsigGate } from '@src/experiments/statsig-integration/use-statsig-gate.hook'
import { isLightColor } from '@src/quince-components/src/atoms/colorFilter/colorFilter.utils'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductOptionDisplayType } from '@src/quince-site-common/src/domains/ProductOption'
import useEddExp from '@src/quince-site-common/src/hooks/edd/hooks/useEddExp'
import {
  generateEDDLabelForBottomTag,
  useEDDDetailFromSelectedOptions,
} from '@src/quince-site-common/src/hooks/edd/utils/edd.utils'
import { useBatchedAnalyticsCaptureForNewProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsCaptureForProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIsVisible from '@src/quince-site-common/src/hooks/useIsVisible'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import { useProductDynamicData } from '@src/quince-site-common/src/redux/globalStateManager/productDynamicDataStateManager'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { getFormattedAverageRating } from '@src/quince-site-common/src/utils/getFormattedAverageRating'
import { getIsHomePage } from '@src/quince-site-common/src/utils/getPageType'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import {
  getProductPropertiesForTrackingNew,
  PRODUCT_GALLERY_MEDIA_TYPE,
} from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import { SWATCH_SIZE, useVisibleSwatchCount } from './color-group/use-visible-swatch-count'

import type { ProductCardProps } from './product-card.types'

// @i18n-ignore — matches ProductCardFooter
const NO_VARIANT_FOUND_ERROR_MESSAGE = 'No variant found for selected option in product card'

const MINI_TILE_VARIANT: ProductTileVariant = 'mini'

const TILE_BODY_TEXT_STYLE = {
  default: css.raw({ textStyle: { base: 'body-12', lg: 'body-16' } }),
  // Same layout as `default` but with body-12 on desktop. Used by 6-up grids
  // (Bestsellers / BackToSchool) where the larger desktop body-16 would crowd
  // the row per Figma.
  defaultSM: css.raw({ textStyle: { base: 'body-12', lg: 'body-12' } }),
  compact: css.raw({ textStyle: { base: 'body-12', lg: 'body-14' } }),
}

const TILE_DEFAULT_META_TEXT_STYLE = css.raw({ textStyle: { base: 'body-12', lg: 'body-14' } })

function ProductCardInner({
  productCard,
  additionalAttributes,
  selectedCardVariantId,
  showSecondImageOnProductTiles,
  forceShowBuyNow,
  tabData,
  productClickTrackConfig,
  searchQuery,
  quickBuyOverride,
  position,
  dsVariant = 'default',
  showWhiteGloveDeliveryTag,
  isPageEligibleForHolidayTag,
  onVariantChange,
  eagerLoadImage,
  quality,
  hideReviews,
  hideWishlistIcon,
  hideBuyNowIcon,
  hideTitle,
  hideProductName,
  hideTags,
  hideOptions,
  customFooter,
  forceUnclickableHref,
  isPostorder,
  gender,
  onClickCb,
  enableImageCarousel = false,
  showBuyNowOnMobile = false,
  ...props
}: ProductCardProps) {
  const product = productCard.product
  const { productId, title, slug, relatedIds, reviews, thirdPartyDetails, flags } = product

  const [selectedVariant, setSelectedVariant] = useState<CardVariant | undefined>(
    getInitialSelectedVariant({ cardVariants: product.cardVariants, selectedCardVariantId }),
  )

  const wrapperRef: RefObject<HTMLDivElement> = useRef(null)
  const { isVisible } = useIsVisible(wrapperRef, 0.85)
  const { isNativeView, deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const { t } = useTranslation('common')
  const { zipCode } = useZipCode()
  const { isEddExpEnabled } = useEddExp()
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()
  const holidayConfig = useStatsigDynamicHolidayConfig()
  const { locale } = useLocaleContext()

  const isMini = dsVariant === 'mini'
  const isCompact = dsVariant === 'compact'
  const isDefaultSM = dsVariant === 'defaultSM'
  const isNarrow = isCompact || isMini

  // `defaultSM` keeps the `default` tile layout (title / options / cart /
  // wishlist all on) but shrinks the desktop body text to body-12 — driven
  // by Figma for 6-up grids where the default body-16 would crowd the row.
  const tileBodyDefault = isCompact
    ? TILE_BODY_TEXT_STYLE.compact
    : isDefaultSM
      ? TILE_BODY_TEXT_STYLE.defaultSM
      : TILE_BODY_TEXT_STYLE.default

  const tileBrand = dsVariant === 'default' ? TILE_DEFAULT_META_TEXT_STYLE : tileBodyDefault

  useEffect(() => {
    setSelectedVariant(getInitialSelectedVariant({ cardVariants: product.cardVariants, selectedCardVariantId }))
  }, [product.cardVariants, selectedCardVariantId])

  const handleVariantChange = useCallback(
    (colorValue: string) => {
      const newVariant = product.cardVariants.find(v => v.displayConfig.value === colorValue)
      if (!newVariant) {
        captureException(new Error(NO_VARIANT_FOUND_ERROR_MESSAGE), {
          feature: FEATURES.UPDATE_COLOR_OPTIONS,
          module: MODULES.PRODUCT_CARD,
          extraInfo: {
            // @i18n-ignore
            capturedFrom: 'handleVariantChange in product-card.tsx',
          },
        })
        return
      }
      setSelectedVariant(newVariant)
      onVariantChange?.(newVariant)

      const oldSelectedOptions = selectedVariant
        ? [{ name: selectedVariant.displayConfig.name, value: selectedVariant.displayConfig.value }]
        : []
      const tags = newVariant.tags?.map(tag => tag.value) ?? []

      trackEvent(TELEMETRY_EVENT_NAMES.VARIANT_SELECT, {
        color: newVariant.displayConfig.value,
        product_id: product.relatedIds.spid,
        internalVariantIds: selectedVariant?.variantIds,
        variantIds: selectedVariant?.svids,
        product_options: oldSelectedOptions,
        tags,
        tag_ids: transformTagsIntoAnalyticsTags(tags),
        stock_info: null,
        entity_id: product.productId,
        // @i18n-ignore
        entity_type: 'product_id',
        productType: product.productTypeName,
        productTitle: product.title,
        variantTitle: newVariant.title,
        price: newVariant.price?.salePrice,
        is_product_card: true,
        division: product.classification?.division,
        department: product.classification?.department,
        subdepartment: product.classification?.subdepartment,
        // eslint-disable-next-line i18next/no-literal-string -- analytics payload; matches ProductCardFooter
        product_option_name: 'Color',
        product_option_value: colorValue,
        element_name: productClickTrackConfig?.context?.contentContainerName || null,
        element_display_name: productClickTrackConfig?.context?.contentContainerDisplayName || null,
        element_type: productClickTrackConfig?.context?.contentContainerType || null,
        element_id: productClickTrackConfig?.context?.trackingId || null,
        element_position: productClickTrackConfig?.context?.contentContainerPosition || null,
        context: {
          productProperties: getProductPropertiesForTrackingNew({
            product,
            productVariant: selectedVariant,
            tags,
          }),
        },
      })
    },
    [product, selectedVariant, productClickTrackConfig, onVariantChange],
  )

  const eddRequestedProducts = {
    product_id: productId.toString(),
    variant_ids: getAllVariantIds(product.cardVariants),
  }

  const {
    productTags: refreshedProductTags,
    isWhiteGloveProduct,
    productEDDDetails,
    productPromoConfig,
    subscriptionEnabled: refreshedSubscriptionEnabled,
  } = useProductDynamicData({
    productId,
    selectedVariantId: selectedVariant?.variantId,
    isProductConstructedOnServer: true,
    skipTagRefresh: props.skipTagRefresh,
    wrapperRef,
    eddRequestedProducts,
    subscriptionExpEnabled: isRecurringItemSubscriptionsExpEnabled && !isPostorder,
  })

  const refreshedTagsForSelectedVariant = useRefreshedTags(product.cardVariants, selectedVariant, refreshedProductTags)

  const { applicableTags: rawApplicableTags } = useMemo(
    () => getVariantTags(selectedVariant, refreshedTagsForSelectedVariant),
    [selectedVariant, refreshedTagsForSelectedVariant],
  )
  const applicableTags = useMemo(() => (hideTags ? [] : rawApplicableTags), [hideTags, rawApplicableTags])

  // Parity with legacy: native-view + not-logged-in always hides the wishlist icon.
  const isLoggedIn = isBrowser() ? AuthUtil.isLoggedIn() : false
  const effectiveHideWishlistIcon = (isNativeView && !isLoggedIn) || hideWishlistIcon

  const rawEddDetails = useEDDDetailFromSelectedOptions(
    productEDDDetails,
    selectedVariant?.variantIds,
    zipCode ?? undefined,
  )

  const isHomepage = getIsHomePage()
  const hideEddOnHomepageRecs = useStatsigGate('disable_edd_homepage_recommendations') && isHomepage
  const eddDetails = props.hideEdd || hideEddOnHomepageRecs ? undefined : rawEddDetails

  const holidayClpMessage = useMemo(() => {
    const displayTags = eddDetails?.displayTags
    if (
      !isPageEligibleForHolidayTag ||
      !displayTags ||
      !Array.isArray(displayTags) ||
      displayTags.length === 0 ||
      !holidayConfig?.tags
    ) {
      return undefined
    }

    for (const displayTag of displayTags) {
      const tagConfig = holidayConfig.tags.find(tag => tag.tag === displayTag && tag.enabled)
      if (tagConfig?.clpTags) {
        const clpTag = tagConfig.clpTags as { message?: Record<string, string> }
        const message = clpTag.message?.[locale] ?? (clpTag.message ? Object.values(clpTag.message)[0] : null) ?? null
        if (message) return message
      }
    }
    return undefined
  }, [isPageEligibleForHolidayTag, eddDetails?.displayTags, holidayConfig?.tags, locale])

  useBatchedAnalyticsCaptureForNewProductCard({
    applicableTags,
    productCard,
    selectedVariant,
    productViewTracker: props.productViewTracker,
    wrapperRef,
    isVisible,
    eddDetails,
  })

  const brandName = thirdPartyDetails.displayName ?? thirdPartyDetails.brand
  const showBrand = Boolean(brandName)

  const subscriptionEnabled = Boolean(isRecurringItemSubscriptionsExpEnabled && refreshedSubscriptionEnabled)

  const formattedRating = getFormattedAverageRating(reviews.averageRating)
  const showRating = !hideReviews && Boolean(formattedRating && Number(formattedRating) > 0)

  const colorVariants = useMemo(
    () => product.cardVariants.filter(v => v.displayConfig.displayType !== ProductOptionDisplayType.TEXT),
    [product.cardVariants],
  )
  const displayedColorVariantValues = useMemo(
    () => colorVariants.map(variant => variant.displayConfig.value),
    [colorVariants],
  )
  const [colorGroupRef, visibleSwatchCount] = useVisibleSwatchCount(colorVariants.length)
  // Parity with legacy `hideOptions`: callers can suppress the entire color picker.
  const showColorGroup = !isMini && !hideOptions && colorVariants.length >= 1

  const deliveryMessage = isEddExpEnabled ? (generateEDDLabelForBottomTag(eddDetails, t) ?? undefined) : undefined

  const isProductWhiteGloveDelivery =
    isWhiteGloveProduct ?? (product.type === PRODUCT_TYPES.NORMAL && Boolean(flags.isWhiteGloveDeliveryAvailable))

  const deliveryLabel =
    showWhiteGloveDeliveryTag && isProductWhiteGloveDelivery ? t('free_white_glove_delivery') : undefined

  const showAddToBag = flags.quickBuyEnabled !== false && !isNativeView && !hideBuyNowIcon
  // eslint-disable-next-line i18next/no-literal-string -- AddToBag reveal mode (matches AddToCartButton / ProductTile), not translatable copy
  const addToBagReveal = forceShowBuyNow ? ('always' as const) : ('hover' as const)
  const revealAddToBagOnHover = showAddToBag && addToBagReveal === 'hover'

  const productSearchObjectId = useMemo(() => {
    if (!selectedVariant || !relatedIds.spid) return ''
    return `${relatedIds.spid}_${selectedVariant.displayConfig.value}`
  }, [selectedVariant, relatedIds.spid])

  const colorDisplayName = selectedVariant?.displayConfig.displayName

  const LinkComponent: FunctionComponent<AnchorHTMLAttributes<HTMLAnchorElement>> = useCallback(
    ({ children, tabIndex }: AnchorHTMLAttributes<HTMLAnchorElement>) => (
      <ProductCardLink
        tabIndex={tabIndex}
        slug={slug}
        additionalAttributes={additionalAttributes}
        selectorKey={props.selectorKey}
        tracker={props.tracker}
        productCardLinkClass={css({
          display: 'block',
          textDecoration: 'none',
        })}
        selectedCardVariant={selectedVariant}
        siteSearchQuery={searchQuery}
        productPosition={position}
        productSearchObjectId={productSearchObjectId}
        title={title}
        spid={relatedIds.spid}
        reviewSummary={reviews}
        productClickTrackConfig={productClickTrackConfig}
        colorVariants={displayedColorVariantValues}
        eddDetails={eddDetails}
        product={product}
        onClickCb={onClickCb}
        allowCustomFooterHover={props.allowCustomFooterHover}
        // Parity with legacy: callers can request that the title/card link be disabled (e.g. on the PDP).
        forceUnclickableHref={forceUnclickableHref}
      >
        {children}
      </ProductCardLink>
    ),
    [
      slug,
      additionalAttributes,
      props.selectorKey,
      props.tracker,
      product,
      productClickTrackConfig,
      selectedVariant,
      searchQuery,
      position,
      productSearchObjectId,
      title,
      relatedIds.spid,
      reviews,
      displayedColorVariantValues,
      eddDetails,
      onClickCb,
      props.allowCustomFooterHover,
      forceUnclickableHref,
    ],
  )

  const colorGroup = useMemo(() => {
    if (!showColorGroup || !selectedVariant) return undefined
    const overflowCount = Math.max(colorVariants.length - visibleSwatchCount, 0)
    const optionName = selectedVariant.displayConfig.name
    return (
      <div
        ref={colorGroupRef}
        className={css({
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 'space-8',
          width: 'full',
          overflow: 'hidden',
          minHeight: `${SWATCH_SIZE}px`,
        })}
      >
        {/* SelectColorGroup only renders the first `maxVisible` children; overflow must live outside Root. */}
        <SelectColorGroup
          variant="compact"
          value={selectedVariant.displayConfig.value}
          maxVisible={visibleSwatchCount}
          onValueChange={handleVariantChange}
          aria-label={t('pc_options_aria', { optionName })}
        >
          {colorVariants.map(v => (
            <Fragment key={v.displayConfig.value}>
              <SelectColorGroupItem
                value={v.displayConfig.value}
                hexColor={v.displayConfig.hexCode ? (v.displayConfig.hexCode as `#${string}`) : undefined}
                imageUrl={!v.displayConfig.hexCode && v.displayConfig.imgURL ? v.displayConfig.imgURL : undefined}
                aria-label={v.displayConfig.displayName ?? v.displayConfig.value}
                addBorder={!v.displayConfig.imgURL && isLightColor(v.displayConfig.hexCode ?? '') ? true : false}
                style={{
                  borderStyle: 'solid',
                }}
              />
            </Fragment>
          ))}
        </SelectColorGroup>
        {overflowCount > 0 ? (
          <span className={css({ flexShrink: 0 })}>
            <LinkComponent>
              <span
                className={css({
                  textStyle: 'body-14',
                  color: 'black',
                  textAlign: 'center',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  whiteSpace: 'nowrap',
                })}
              >
                {`+${overflowCount}`}
              </span>
            </LinkComponent>
          </span>
        ) : null}
      </div>
    )
  }, [
    showColorGroup,
    colorVariants,
    visibleSwatchCount,
    selectedVariant,
    handleVariantChange,
    t,
    LinkComponent,
    colorGroupRef,
  ])

  const imageOverlay = (
    <div
      className={css({
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        '& a': {
          position: 'absolute',
          inset: 0,
          display: 'block',
        },
      })}
      aria-hidden="true"
    >
      <LinkComponent tabIndex={-1}>
        <span />
      </LinkComponent>
    </div>
  )

  // Parity with legacy: legacy ProductImageSection resolves images via
  // `useProductImageProps` → `getImagesForOptions(selectedCardVariant, gender)`,
  // which filters out images whose `description` is 'MALE'/'FEMALE' but doesn't
  // match the current page's gender context. Using `selectedVariant.images`
  // directly leaks the wrong gender's photos.
  const rawImages = useMemo(() => getImagesForOptions(selectedVariant, gender), [selectedVariant, gender])

  // Statsig-gated, mobile-web only: render the per-variant images as a swipeable carousel inside the
  // card. When on, the slides carry their own PDP links (`renderCarouselSlideLink`), so the full-bleed
  // tap overlay below is suppressed to let Embla receive the swipe gesture.
  const showImageCarousel = enableImageCarousel && isMobile && rawImages.length > 1

  // Mirrors the PDP gallery-swipe event (`productGalleryViewed`) for the in-grid carousel, adding the
  // CLP fields: media type, product position (already in the click-track context), and product id.
  const handleCarouselSlideView = useCallback(
    (index: number) => {
      const image = rawImages[index]
      if (!image) return
      const tags = applicableTags.map(tag => tag.value)
      trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_VIEWED, {
        product_id: String(relatedIds.spid),
        context: {
          product_position: position,
          productProperties: {
            ...getProductPropertiesForTrackingNew({ product, productVariant: selectedVariant, tags }),
            product_gallery_photo_id: image.id,
            product_gallery_photo_position: index + 1,
            product_gallery_media_type: PRODUCT_GALLERY_MEDIA_TYPE.PHOTO,
          },
        },
      })
    },
    [rawImages, applicableTags, product, selectedVariant, relatedIds.spid, position],
  )

  const renderCarouselSlideLink = useCallback(
    (node: ReactNode): ReactNode => <LinkComponent>{node}</LinkComponent>,
    [LinkComponent],
  )

  return (
    <div ref={wrapperRef} className={css({ position: 'relative', overflow: 'hidden' })}>
      <article
        data-testid={TEST_LOCATORS.PRODUCT_CARD}
        data-slot="product-tile"
        data-variant={dsVariant}
        data-slug={slug}
        className={css(
          { display: 'flex', flexDirection: 'column', ...(!isMini ? { gap: 'space-8' } : {}), width: 'full' },
          revealAddToBagOnHover ? addToCartButtonHoverRevealParentCss : {},
          productTileImageHoverSwapCss,
        )}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            width: 'full',
          })}
        >
          <div
            data-slot="product-tile-hover-image-area"
            className={css({ position: 'relative', width: 'full', aspectRatio: '4 / 5' })}
          >
            <ImageTile
              images={rawImages}
              variantFallbackAlt={[selectedVariant?.displayConfig.displayName, title].filter(Boolean).join(' ')}
              applicableTags={applicableTags}
              readyToShipEnabled={Boolean(flags.readyToShipEnabled)}
              showSecondImageOnProductTiles={showSecondImageOnProductTiles}
              imageLoading={eagerLoadImage ? 'eager' : 'lazy'}
              imageQuality={quality}
              tileVariant={dsVariant}
              enableImageCarousel={showImageCarousel}
              renderSlideLink={renderCarouselSlideLink}
              onCarouselSlideView={handleCarouselSlideView}
            />
            {showImageCarousel ? null : imageOverlay}
            <AddToWishlistButton
              spid={relatedIds.spid}
              selectedColor={selectedVariant?.displayConfig.value ?? ''}
              wishlistTrackConfig={props.wishlistTrackConfig}
              wishlistEnabled={flags.wishlistEnabled !== false}
              hideWishlistIcon={effectiveHideWishlistIcon}
              productName={[colorDisplayName, title].filter(Boolean).join(' ')}
              tileVariant={dsVariant}
            />
            <AddToCartButton
              productCard={productCard}
              selectedVariant={selectedVariant}
              productClickTrackConfig={productClickTrackConfig}
              searchQuery={searchQuery}
              position={position}
              tabData={tabData as Record<string, string | boolean> | undefined}
              quickBuyOverride={quickBuyOverride}
              visible={showAddToBag}
              addToBagReveal={addToBagReveal}
              tileVariant={dsVariant}
              productSearchObjectId={productSearchObjectId}
              showBuyNowOnMobile={showBuyNowOnMobile}
            />
          </div>

          {isMini ? (
            <div
              data-slot="product-tile-hover-price-area"
              className={css({ paddingTop: isNarrow ? 'space-8' : 'space-12' })}
            >
              <ProductTilePrice
                selectedVariant={selectedVariant}
                subscriptionEnabled={subscriptionEnabled}
                productPromoConfig={productPromoConfig}
                tileVariant={MINI_TILE_VARIANT}
              />
            </div>
          ) : (
            // Body — two-column layout per Figma DS 2.0 (4218:31444) applies ONLY
            // to Name (+Brand) + Rating on the left and Price on the right. The
            // color group, delivery message and tag rows sit BELOW the two-column
            // row at full width, so a long sale + compare-at price doesn't squeeze
            // the swatches into a narrow column and force them to wrap.
            // Spacing between the columns lives on the price column's `paddingLeft`,
            // not a parent `gap`, so the gutter belongs to the price side.
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: 'space-8',
                width: 'full',
                minWidth: 0,
              })}
            >
              <div
                className={css({
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  width: 'full',
                  minWidth: 0,
                })}
              >
                <div
                  className={css({
                    flex: '1 1 0',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'space-8',
                    alignItems: 'flex-start',
                  })}
                >
                  {!hideTitle && (!hideProductName || (showBrand && brandName)) ? (
                    // Name sub-group per Figma DS 2.0 (4218:31446 / 905:11047 / 905:11234):
                    // title and brand stack with a `space-4` inner gap, *inside* the
                    // body header column (which uses `space-8` between blocks).
                    <div
                      className={css({
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'space-4',
                        width: 'full',
                      })}
                    >
                      {!hideProductName ? (
                        <div
                          data-slot="product-tile-hover-title-area"
                          className={css(tileBodyDefault, {
                            position: 'relative',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            color: 'black',
                            width: 'full',
                            // paddingTop here (instead of a parent column `gap` or an
                            // `::after` hit-zone on the image area) is what creates
                            // the visual space between the image and the body — and
                            // because the padding is part of the title element, the
                            // would-be gap still lands inside `[data-slot=
                            // "product-tile-hover-title-area"]:hover`, so the image
                            // swap fires when the cursor crosses it.
                            paddingTop: isNarrow ? 'space-8' : 'space-12',
                          })}
                        >
                          <ProductTileTitle
                            title={title}
                            colorDisplayName={colorDisplayName}
                            renderLink={node => <LinkComponent>{node}</LinkComponent>}
                          />
                        </div>
                      ) : null}
                      {showBrand && brandName ? (
                        <div
                          className={css(tileBrand, {
                            color: 'mediumgray',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: 'full',
                          })}
                        >
                          {brandName}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {showRating ? (
                    <div
                      className={css({
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 'space-4',
                        alignItems: 'center',
                      })}
                    >
                      {/* eslint-disable-next-line i18next/no-literal-string -- DS StarRating variant token, not translatable copy */}
                      <StarRating
                        variant="black"
                        css={{ flexShrink: 0, fontSize: '16px', width: '16px', height: '16px' }}
                      />
                      <span
                        className={css({
                          textStyle: { base: 'body-12-strong', lg: 'body-14-strong' },
                          color: 'black',
                        })}
                      >
                        {formattedRating as StarRatingValue}
                      </span>
                    </div>
                  ) : null}
                </div>
                <div
                  data-slot="product-tile-hover-price-area"
                  className={css({
                    flexShrink: 0,
                    paddingLeft: isCompact ? 'space-10' : 'space-8',
                    paddingTop: isNarrow ? 'space-8' : 'space-12',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                  })}
                >
                  <ProductTilePrice
                    selectedVariant={selectedVariant}
                    subscriptionEnabled={subscriptionEnabled}
                    productPromoConfig={productPromoConfig}
                    tileVariant={dsVariant}
                  />
                </div>
              </div>

              {(colorGroup ?? deliveryMessage ?? holidayClpMessage ?? deliveryLabel) ? (
                <div
                  className={css({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'space-8',
                    alignItems: 'flex-start',
                    width: 'full',
                  })}
                >
                  {colorGroup}

                  {deliveryMessage ? (
                    <div
                      className={css(
                        // `default` desktop tile: body-14 per Figma (852:22758); other variants stay body-12.
                        dsVariant === 'default' ? TILE_DEFAULT_META_TEXT_STYLE : { textStyle: 'body-12' },
                        {
                          color: 'green-100',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          width: 'full',
                        },
                      )}
                    >
                      {deliveryMessage}
                    </div>
                  ) : null}

                  {holidayClpMessage ? <Tag variant="green">{holidayClpMessage}</Tag> : null}

                  {deliveryLabel ? <Tag variant="offwhite">{deliveryLabel}</Tag> : null}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {!isMini && customFooter ? <LinkComponent>{customFooter}</LinkComponent> : null}
      </article>
    </div>
  )
}

/**
 * Full business-logic product card that composes DS blocks (ImageTile, Title, Price, AddToCart, AddToWishlist)
 * with parity to {@link ProductCardRenderer} / legacy product card behavior.
 */
export function ProductCard(props: ProductCardProps) {
  return (
    <ProductCardHoverProvider>
      <ProductCardInner {...props} />
    </ProductCardHoverProvider>
  )
}
